import React, { useEffect, useState } from 'react';

import { motion, AnimatePresence } from 'framer-motion';

import { useInitializationProgress } from 'hooks/useInitializationProgress';
import { cn } from 'lib/utils';
import { useThemeContext } from 'providers/ThemeProvider';

/**
 * Centralized loading component for initialization
 * Discreet design focused on user experience
 * Migrated from MUI to Framer Motion + Tailwind CSS
 */
const InitializationLoader: React.FC = () => {
  const theme = useThemeContext();
  const { overallProgress, isInitializing, initializationStatus, hasInitialized } =
    useInitializationProgress();

  const [showLoader, setShowLoader] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  // Controls when to show/hide the loader
  useEffect(() => {
    if (hasInitialized && !isExiting) {
      // Longer delay to give time to read the messages
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          setShowLoader(false);
        }, 500);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [hasInitialized, isExiting]);

  if (!theme || !showLoader) {
    return null;
  }

  const shouldShow = showLoader && !isExiting;

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          data-testid="initialization-loader"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            'fixed inset-0 z-[9999]',
            'flex flex-col items-center justify-center',
            'bg-background'
          )}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-center max-w-[400px] w-full px-4"
          >
            {/* Simplified logo */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="relative w-20 h-20 rounded-full flex items-center justify-center mb-6 overflow-hidden"
              style={{ backgroundColor: theme.primary }}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 rounded-full overflow-hidden">
                <div
                  className="absolute inset-0 animate-shimmer"
                  style={{
                    background:
                      'linear-gradient(45deg, transparent 30%, hsl(var(--foreground) / 0.2) 50%, transparent 70%)',
                  }}
                />
              </div>
              <span className="text-3xl font-bold text-foreground relative z-10">A</span>
            </motion.div>

            {/* Main message */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9 }}
              className="text-center mb-6"
            >
              <h6 className="text-lg font-medium text-foreground mb-1">
                {initializationStatus.message}
              </h6>
              <p className="text-sm text-muted-foreground">{initializationStatus.description}</p>
            </motion.div>

            {/* Discreet progress bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              data-testid="progress-bar-container"
              className="w-full mb-4"
            >
              <div
                className={cn(
                  'h-1.5 rounded-full relative overflow-hidden',
                  'bg-muted shadow-inner'
                )}
              >
                {/* Progress bar fill */}
                <div
                  className={cn(
                    'absolute top-0 left-0 h-full rounded-full',
                    'transition-all duration-800 ease-out',
                    overallProgress > 0 && overallProgress < 100 && 'animate-progress-pulse'
                  )}
                  style={{
                    width: `${overallProgress}%`,
                    backgroundColor: theme.primary,
                  }}
                />
                {/* Shimmer overlay on progress */}
                {overallProgress > 0 && overallProgress < 100 && (
                  <div
                    className="absolute top-0 left-0 h-full animate-progress-shimmer"
                    style={{
                      width: `${overallProgress}%`,
                      background:
                        'linear-gradient(90deg, transparent 0%, hsl(var(--foreground) / 0.4) 50%, transparent 100%)',
                    }}
                  />
                )}
              </div>
              <span
                className={cn(
                  'block text-center mt-2 text-xs font-medium',
                  'transition-all duration-300',
                  overallProgress === 100 ? 'animate-pulse' : 'text-muted-foreground'
                )}
                style={{
                  color: overallProgress === 100 ? theme.primary : undefined,
                }}
              >
                {overallProgress}%
              </span>
            </motion.div>

            {/* Discreet loading indicator */}
            {isInitializing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.2 }}
                data-testid="loading-dots"
                className="flex gap-1"
              >
                {[0, 1, 2].map(index => (
                  <div
                    key={index}
                    className="w-1.5 h-1.5 rounded-full animate-bounce-dot"
                    style={{
                      backgroundColor: theme.primary,
                      animationDelay: `${-0.24 + index * 0.12}s`,
                    }}
                  />
                ))}
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InitializationLoader;
